/**
 * Platform admins feature — Zod schemas.
 *
 * The response shapes (an admin_users row, its list page and the invite
 * response) are generated from the operations contract into
 * ./api.generated.ts and re-exported here; this file keeps only the invite
 * and role forms. The role and status vocabularies are the contract's:
 * admin_users.role holds exactly the six stored roles (organization roles
 * live in admin_org_grants and are never an admin_users row).
 */

import { z } from "zod";
import {
  inviteAdminResponseSchema,
  platformAdminSchema,
  platformAdminStatusSchema,
  platformAdminsListResponseSchema,
} from "./api.generated";

export {
  inviteAdminResponseSchema,
  platformAdminSchema,
  platformAdminStatusSchema,
  platformAdminsListResponseSchema,
};

// ── Role / status vocabularies ────────────────────────────────────────────

/** The six roles an admin_users row can hold (the contract's AdminUserRole). */
export const adminRoleSchema = platformAdminSchema.shape.role;
export type AdminRole = z.infer<typeof adminRoleSchema>;
/** Narrows an RBAC catalog role to one an admin_users row can hold. */
export function isAdminRole(role: string): role is AdminRole {
  return adminRoleSchema.safeParse(role).success;
}
export const platformAdminRoleSchema = adminRoleSchema;
export type PlatformAdminRole = AdminRole;
export type PlatformAdminStatus = z.infer<typeof platformAdminStatusSchema>;
export type AdminKind = z.infer<typeof platformAdminSchema.shape.kind>;

// ── Row shape ─────────────────────────────────────────────────────────────

export type PlatformAdmin = z.infer<typeof platformAdminSchema>;
export type PlatformAdminsListResponse = z.infer<typeof platformAdminsListResponseSchema>;

// ── Invite input ──────────────────────────────────────────────────────────

// Match the admin service's phone format without inferring a country code.
const adminPhonePattern = /^\+[1-9]\d{1,14}$/;

export function normalizeAdminPhone(value: string): string {
  const trimmed = value.trim();
  const compact = trimmed.replace(/[\s().-]/g, "");
  const international = compact.startsWith("+") ? compact : `+${compact}`;
  // Preserve invalid input for field validation; never discard letters or
  // extensions and silently turn them into a different phone number.
  return adminPhonePattern.test(international) ? international : trimmed;
}

/**
 * Invite input. Fields are tied to the backend's
 * CreateAdminUserRequest shape. Cross-field rules enforced via
 * superRefine rather than separate schemas so react-hook-form's
 * SubmitHandler typing stays clean:
 *
 *   - Platform roles require a phone (backend's admin service
 *     rejects activation without one; we fail at form submit so
 *     the operator sees a clear error instead of a server 4xx).
 *   - Tenant / org roles require a tenant_id; the backend rejects
 *     invites with the wrong scope, so we block them client-side.
 */
export const inviteAdminSchema = z
  .object({
    email: z.string().email("Enter a valid email"),
    name: z.string().min(1, "Name is required").max(255),
    phone: z.string().transform(normalizeAdminPhone).refine(
      (value) => value === "" || adminPhonePattern.test(value),
      "Enter a phone number with its country code, for example +14155551234. Use 2–15 digits and no extension.",
    ).optional(),
    role: adminRoleSchema,
    tenant_id: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    const isPlatform = val.role === "platform_admin" || val.role === "platform_operator";
    if (isPlatform && (!val.phone || val.phone.trim() === "")) {
      ctx.addIssue({
        code: "custom",
        path: ["phone"],
        message: "Phone is required for platform-scope roles.",
      });
    }
    if (!isPlatform && (!val.tenant_id || val.tenant_id.trim() === "")) {
      ctx.addIssue({
        code: "custom",
        path: ["tenant_id"],
        message: "Select a tenant for tenant- or organization-scope roles.",
      });
    }
  });
export type InviteAdminInput = z.infer<typeof inviteAdminSchema>;

// Back-compat alias for existing imports.
export const invitePlatformAdminSchema = inviteAdminSchema;
export type InvitePlatformAdminInput = InviteAdminInput;

export type InviteAdminResponse = z.infer<typeof inviteAdminResponseSchema>;

// ── Role update ───────────────────────────────────────────────────────────

export const updatePlatformAdminRoleSchema = z.object({
  role: adminRoleSchema,
});
export type UpdatePlatformAdminRoleInput = z.infer<typeof updatePlatformAdminRoleSchema>;
